import { type SxProps, type Theme } from "@mui/material/styles";

import { mergeSx } from "ui/merge-sx";
import {
  MENU_ITEM_ICON_SIZE,
  MENU_ITEM_SPACING,
  MENU_ITEM_TYPE,
} from "ui/actions-menu/menu-item/menu-item.constants";

import { focusRing } from "theme/theme.helpers";

import { borderRadius } from "design-tokens";

/**
 * Ports the Tailwind the item carried before it was built on MUI. `MenuItem`'s own gutters and
 * `minHeight` are zeroed because the padding sits on the content row instead, which is what lets the
 * hover fill cover the whole row the way the old full-width button did.
 */
export const ITEM_SX: SxProps<Theme> = (theme) => ({
  ...MENU_ITEM_TYPE,
  minHeight: 0,
  /**
   * Longhands rather than `padding: 0`, paired with `disableGutters` on the item. A shorthand does
   * beat `MenuItem`'s own `paddingTop`/`paddingBottom` in a browser, but jsdom's CSSOM keeps
   * reporting the longhands — so the shorthand form leaves `css-cascade` tests reading 6px and 16px
   * that never paint, and no test here can be trusted. Stating it property-for-property makes the
   * two agree. All of the row's padding is the row's.
   */
  paddingTop: 0,
  paddingBottom: 0,
  /**
   * The panel is a flex column, so a `div` child would stretch on its own — a `<button>` does not,
   * and the hover fill then stops at the label instead of reaching the panel edge. This is the
   * `w-full` the item used to carry, and it has to stay stated.
   */
  width: "100%",
  borderRadius: borderRadius.md,
  color: theme.palette.text.primary,

  "&:hover, &:focus": {
    backgroundColor: theme.palette.grey[100],
  },

  "&.Mui-focusVisible": {
    backgroundColor: theme.palette.grey[100],
    boxShadow: focusRing(theme.palette.primary.main),
  },

  // MUI tints the selected row with `primary` at `action.selectedOpacity`; the design is a flat grey.
  "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
    backgroundColor: theme.palette.grey[200],
  },

  /**
   * `pointerEvents` is restored because the object form of `tooltip` hangs an interactive icon inside
   * a disabled row, and `ButtonBase` would otherwise swallow its hover. Clicks stay blocked either
   * way — `useButtonBase` calls `preventDefault` and returns before any `onClick` when disabled.
   */
  "&.Mui-disabled, &.Mui-disabled:hover": {
    opacity: 1,
    backgroundColor: "transparent",
    color: theme.palette.text.disabled,
    cursor: "not-allowed",
    pointerEvents: "auto",
  },
});

/**
 * Figma's `Type=Critical`: the label turns `error`; hover and selected fills stay grey. A plain `color`
 * against `ITEM_SX`'s `&.Mui-disabled`, so a disabled critical row still greys out.
 */
export const CRITICAL_ITEM_SX: SxProps<Theme> = mergeSx(ITEM_SX, (theme: Theme) => ({
  color: theme.palette.error.main,
}));

/**
 * The leading glyph's slot. It owns the three things every call site used to repeat — the size, the
 * colour and `aria-hidden` — so the icon prop can be a bare `<Icons.Foo />`.
 *
 * The colour is pinned rather than inherited: Figma keeps the glyph on `text.secondary` beside a
 * `text.primary` label, and darkens it only on a selected row; a critical row keeps it `error` in every
 * state but disabled. Selected and disabled reach it through `.Mui-* &`, which carries two classes
 * against the resting rule's one. The trailing tooltip icon sits outside the slot and keeps its own
 * semantic colour.
 *
 * `:where` deliberately drops the size rule to zero specificity, leaving it a *default*: the sidebar
 * flyouts share their `size-4` icons with the sidebar's own nav rows, so that class has to keep
 * winning here without the constants being forked.
 */
export const ICON_SX: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexShrink: 0,
  color: theme.palette.text.secondary,

  ":where(&) > svg": {
    width: MENU_ITEM_ICON_SIZE,
    height: MENU_ITEM_ICON_SIZE,
  },

  ".Mui-selected &": {
    color: theme.palette.text.primary,
  },

  ".Mui-disabled &": {
    color: theme.palette.text.disabled,
  },
});

/** Restates the selected rule too, but not the disabled one, which keeps winning on specificity. */
export const CRITICAL_ICON_SX: SxProps<Theme> = mergeSx(ICON_SX, (theme: Theme) => ({
  color: theme.palette.error.main,

  ".Mui-selected &": {
    color: theme.palette.error.main,
  },
}));

export const CONTENT_SX: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  gap: MENU_ITEM_SPACING.containerGap,
  py: MENU_ITEM_SPACING.py,
  px: MENU_ITEM_SPACING.px,
};

/**
 * Glyph and text sit in a group of their own because Figma spaces them tighter than the row spaces the
 * group from a trailing icon. It grows so that trailing affordance takes the right edge.
 */
export const LABEL_GROUP_SX: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  flexGrow: 1,
  minWidth: 0,
  gap: MENU_ITEM_SPACING.labelGap,
};

export const LABEL_SX: SxProps<Theme> = { color: "inherit", textAlign: "left" };

export const ROW_TOOLTIP_WRAPPER_SX: SxProps<Theme> = { display: "block", width: "100%" };

export const ROW_TOOLTIP_SX: SxProps<Theme> = { textAlign: "left" };

export const TRAILING_TOOLTIP_SX: SxProps<Theme> = { textAlign: "center" };
